import { useMemo } from "react";
import { Link } from "react-router-dom";
import { movies, type Movie } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import useDocumentTitle from "@/hooks/useDocumentTitle";

type Collection = {
  id: string;
  title: string;
  desc: string;
  match: (m: Movie) => boolean;
};

const collections: Collection[] = [
  {
    id: "marriage",
    title: "婚姻与信任的边界",
    desc: "当亲密关系遭遇考验，信任与占有欲之间的界线变得模糊。",
    match: (m) => m.tags.includes("婚姻") || m.tags.includes("信任") || m.genres.includes("爱情"),
  },
  {
    id: "suspense",
    title: "层层反转的悬疑",
    desc: "真相被一层层剥开，直到最后一刻才看清全貌。",
    match: (m) => m.genres.includes("悬疑") || m.genres.includes("惊悚"),
  },
  {
    id: "memory",
    title: "记忆与往事",
    desc: "被时间封存的故事，终有被重新翻开的一天。",
    match: (m) =>
      m.tags.some((t) => ["回忆", "往事", "记忆", "书信", "青春"].includes(t)) ||
      m.genres.includes("心理"),
  },
];

export default function Recommendations() {
  useDocumentTitle("相关推荐 - 交换朋友的妻子影视内容");

  const grouped = useMemo(
    () =>
      collections.map((c) => ({
        ...c,
        list: movies.filter((m) => m.id !== "1" && c.match(m)).slice(0, 8),
      })),
    []
  );

  return (
    <div className="bg-background-950 pt-24 md:pt-28 pb-16 md:pb-20">
      <div className="mx-auto max-w-site px-4 md:px-6">
        <nav className="text-xs text-foreground-500 mb-5 flex items-center gap-2">
          <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-300">相关推荐</span>
        </nav>

        <header className="mb-10 md:mb-14 max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/15 border border-accent-500/40 text-accent-300 text-xs font-label tracking-wide">
            <i className="ri-compass-3-line"></i> 主题推荐专题
          </span>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-50">
            与《交换朋友的妻子》主题相近的影片
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-400 leading-relaxed">
            我们从婚姻、信任、悬疑反转与记忆往事等角度，整理了若干个推荐专题。无论你想继续沉浸在情感张力里，还是寻找一部烧脑的悬疑片，都能在这里找到下一部想看的电影。
          </p>
        </header>

        <div className="space-y-12 md:space-y-16">
          {grouped.map((c) => (
            <section key={c.id}>
              <div className="flex items-end justify-between gap-4 mb-6">
                <div>
                  <h2 className="font-heading text-lg md:text-xl text-foreground-50 flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-accent-500"></span> {c.title}
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">{c.desc}</p>
                </div>
                <Link
                  to="/category"
                  className="shrink-0 text-sm text-foreground-400 hover:text-accent-400 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  进入分类 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              {c.list.length === 0 ? (
                <div className="rounded-lg border border-dashed border-background-700 p-8 text-center text-sm text-foreground-500">
                  该专题暂无推荐内容。
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {c.list.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            <i className="ri-home-5-line"></i> 返回首页
          </Link>
          <Link
            to="/category"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-600 text-foreground-100 text-sm font-medium hover:border-accent-500 hover:text-accent-400 transition-colors"
          >
            <i className="ri-layout-grid-line"></i> 浏览电影分类
          </Link>
        </div>
      </div>
    </div>
  );
}